import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Brain,
  Loader2,
  Send,
  Sparkles,
  Wrench,
  ExternalLink,
  RotateCcw,
} from 'lucide-react';

import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { Button } from '@/components/ui/button';
import { BrainShell } from './BrainSegments';
import {
  BRAIN_SUGGESTIONS,
  BrainBlock,
  BrainMessage,
  makeUserMessage,
  mockBrainReply,
  welcomeMessage,
} from './mockBrain';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function BlockView({ block }: { block: BrainBlock }) {
  if (block.type === 'text') {
    return (
      <div className="whitespace-pre-wrap text-sm leading-relaxed text-gray-800">
        {block.text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return (
              <strong key={i} className="font-semibold text-gray-900">
                {part.slice(2, -2)}
              </strong>
            );
          }
          return <span key={i}>{part}</span>;
        })}
      </div>
    );
  }

  if (block.type === 'metric') {
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
        <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
          {block.label}
        </p>
        <p className="text-xl font-semibold tabular-nums text-gray-900">{block.value}</p>
        {block.hint ? <p className="text-xs text-gray-500">{block.hint}</p> : null}
      </div>
    );
  }

  if (block.type === 'table') {
    return (
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full text-left text-xs">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              {block.columns.map((c) => (
                <th key={c} className="px-3 py-2 font-medium">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {block.rows.map((row, ri) => (
              <tr key={ri} className="text-gray-800">
                {row.map((cell, ci) => (
                  <td key={ci} className="px-3 py-2">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (block.type === 'links') {
    return (
      <div className="flex flex-wrap gap-2">
        {block.items.map((item) => (
          <Link
            key={item.href + item.label}
            to={item.href}
            className="inline-flex items-center gap-1 rounded-md border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-800 hover:bg-blue-100"
          >
            {item.label}
            <ExternalLink className="h-3 w-3 opacity-60" />
          </Link>
        ))}
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-1.5 rounded-md border border-amber-100 bg-amber-50 px-2 py-1 text-[11px] text-amber-900">
      <Wrench className="h-3 w-3" />
      <span className="font-mono">{block.name}</span>
      <span className="text-amber-700/80">· {block.detail}</span>
    </div>
  );
}

function MessageBubble({ message }: { message: BrainMessage }) {
  const isUser = message.role === 'user';
  const metrics = message.blocks.filter((b) => b.type === 'metric');
  const rest = message.blocks.filter((b) => b.type !== 'metric');

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[min(100%,40rem)] space-y-2 rounded-2xl px-4 py-3 ${
          isUser
            ? 'bg-blue-700 text-white'
            : 'border border-gray-200 bg-white text-gray-900 shadow-sm'
        }`}
      >
        {!isUser && (
          <div className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-400">
            <Brain className="h-3.5 w-3.5 text-blue-600" />
            Brain
          </div>
        )}
        {isUser ? (
          <p className="whitespace-pre-wrap text-sm leading-relaxed">
            {message.blocks[0]?.type === 'text' ? message.blocks[0].text : ''}
          </p>
        ) : (
          <>
            {metrics.length > 0 && (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {metrics.map((b, i) => (
                  <BlockView key={i} block={b} />
                ))}
              </div>
            )}
            {rest.map((b, i) => (
              <BlockView key={i} block={b} />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

const BrainAgentPage = () => {
  useBreadcrumbs([{ label: 'Brain' }, { label: 'Chat' }]);

  const [messages, setMessages] = useState<BrainMessage[]>(() => [welcomeMessage()]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  const runTurn = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || thinking) return;
    setInput('');
    setMessages((prev) => [...prev, makeUserMessage(trimmed)]);
    setThinking(true);
    await sleep(550 + Math.random() * 700);
    const reply = mockBrainReply(trimmed);
    setMessages((prev) => [...prev, reply]);
    setThinking(false);
    inputRef.current?.focus();
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void runTurn(input);
  };

  const reset = () => {
    setMessages([welcomeMessage()]);
    setInput('');
    setThinking(false);
  };

  return (
    <BrainShell
      fullHeight
      title="Chat"
      description="Mock admin co-pilot — ask about data, queues, and drafts across the console."
      actions={
        <Button variant="outline" size="sm" className="gap-1.5" onClick={reset}>
          <RotateCcw className="h-3.5 w-3.5" />
          New chat
        </Button>
      }
    >
      <div className="mx-auto flex w-full max-w-3xl min-h-0 flex-1 flex-col overflow-hidden px-4">
        <div className="flex-1 space-y-4 overflow-y-auto py-6">
          {messages.map((m) => (
            <MessageBubble key={m.id} message={m} />
          ))}
          {thinking && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
              Brain is checking mock admin data…
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div className="pb-4">
          <div className="mb-3 flex flex-wrap gap-2">
            {BRAIN_SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                disabled={thinking}
                onClick={() => void runTurn(s)}
                className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-700 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-800 disabled:opacity-50"
              >
                <Sparkles className="h-3 w-3 text-blue-500" />
                {s}
              </button>
            ))}
          </div>

          <form
            onSubmit={onSubmit}
            className="rounded-xl border border-gray-200 bg-white p-2 shadow-sm focus-within:border-blue-300 focus-within:ring-2 focus-within:ring-blue-100"
          >
            <textarea
              ref={inputRef}
              rows={2}
              value={input}
              disabled={thinking}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void runTurn(input);
                }
              }}
              placeholder="Ask Brain about CMS, listings, payments, users…"
              className="max-h-40 w-full resize-none border-0 bg-transparent px-2 py-2 text-sm text-gray-900 outline-none placeholder:text-gray-400"
            />
            <div className="flex items-center justify-between gap-2 px-1 pb-1">
              <p className="text-[11px] text-gray-400">
                Mock mode · Enter to send · Shift+Enter for newline
              </p>
              <Button type="submit" size="sm" className="gap-1.5" disabled={thinking || !input.trim()}>
                {thinking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Send
              </Button>
            </div>
          </form>
        </div>
      </div>
    </BrainShell>
  );
};

export default BrainAgentPage;
